import { HubConnectionState } from "@microsoft/signalr";
import { useCallback, useEffect, useRef } from "react";
import { createSocketInstance } from "~/helpers/socket";
import { SocketInstance, SocketMethodNames } from "~/types/socket";
import { useRenewToken } from "./useRenewToken";
import { useAuthenticationContext } from "~/providers/AuthenticationProvider";
const sockets: SocketInstance = {};

const isProduction = process.env.NODE_ENV === "production";
const RECONNECT_TIME = isProduction ? 10000 : 1000;
const RECONNECT_COUNT_LIMIT = isProduction ? 60 : 0;
let RECONNECT_COUNTER = RECONNECT_COUNT_LIMIT;

export const useAddSocketListener = (
  socketUrl: string,
  keyOfSocket: string,
  callBacks: Partial<Record<SocketMethodNames, (message: string) => void>>,
) => {
  const timeoutRef = useRef<NodeJS.Timeout | undefined>();

  const renewToken = useRenewToken();

  const { handleSetToken } = useAuthenticationContext();

  const createSocket = useCallback(() => {
    let socket = sockets[keyOfSocket];

    if (!socket) {
      sockets[keyOfSocket] = createSocketInstance(
        socketUrl,
        sockets[keyOfSocket],
      );
      socket = sockets[keyOfSocket];
    }

    Object.entries(callBacks).forEach(([methodName, callBackMethod]) => {
      socket?.on(methodName, (message: string) => {
        callBackMethod(message);
      });
    });

    if (socket?.state === HubConnectionState.Disconnected) {
      socket
        ?.start()
        .then(() => {
          RECONNECT_COUNTER = RECONNECT_COUNT_LIMIT;
        })
        .catch(async () => {
          if (timeoutRef.current) clearTimeout(timeoutRef.current);

          if (RECONNECT_COUNTER > 0) {
            RECONNECT_COUNTER--;
            timeoutRef.current = setTimeout(async () => {
              try {
                const renewTokenResult = await renewToken();
                if (renewTokenResult) {
                  handleSetToken(renewTokenResult);
                }
                if (sockets[keyOfSocket]) {
                  await sockets[keyOfSocket].stop();
                  delete sockets[keyOfSocket];
                }
                createSocket();
              } catch (error) {
                // eslint-disable-next-line no-console
                console.error("Token renewal failed", error);
              }
            }, RECONNECT_TIME);
          } else {
            // COMMENT: yusuf, this is written already in the start catch event
            // console.error("Socket Error", err);
          }
        });
    }

    socket?.onclose(() => {
      if (socket?.state === HubConnectionState.Disconnected) {
        if (RECONNECT_COUNTER > 0) {
          createSocket();
        }
      }
    });
  }, [callBacks, keyOfSocket, socketUrl, renewToken, handleSetToken]);

  useEffect(() => {
    createSocket();

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
